import { useEffect, useState, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import ExpenseFormDialog from "@/components/dialogs/ExpenseFormDialog";
import { ReconciliationNoteButton } from "@/components/ReconciliationNoteButton";
import api, { fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Download, FileSpreadsheet, FileText, Pencil, Trash2, Search, Wallet, CalendarDays, LayoutGrid, Receipt } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";

const COLORS = ["#00823B", "#76C121", "#1C1C1E", "#4B5563", "#0EA5A0", "#B45309", "#7C3AED", "#DC2626", "#0284C7", "#65A30D", "#DB2777", "#475569", "#059669"];
const currency = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function ExpenseTracker() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [expenses, setExpenses] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [options, setOptions] = useState({ categories: [], departments: [], payment_methods: [] });
  const [filters, setFilters] = useState({ date_from: "", date_to: "", category: "all", department: "all", payment_method: "all", search: "" });
  const [formOpen, setFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const loadOptions = () => api.get("/expenses/categories").then((r) => setOptions(r.data)).catch(() => {});
  const loadDashboard = () => api.get("/expenses/dashboard").then((r) => setDashboard(r.data)).catch(() => {});

  const loadExpenses = () => {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.category !== "all") params.category = filters.category;
    if (filters.department !== "all") params.department = filters.department;
    if (filters.payment_method !== "all") params.payment_method = filters.payment_method;
    if (filters.search) params.search = filters.search;
    api.get("/expenses", { params }).then((r) => setExpenses(r.data)).catch(() => {});
  };

  useEffect(() => { loadOptions(); loadDashboard(); }, []);
  useEffect(() => { loadExpenses(); }, [filters]);

  const refreshAll = () => { loadExpenses(); loadDashboard(); };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      refreshAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteAttachment = async (id) => {
    try {
      await api.delete(`/expenses/${id}/attachment`);
      toast.success("Attachment removed");
      refreshAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportFile = async (type) => {
    const params = new URLSearchParams();
    if (filters.date_from) params.append("date_from", filters.date_from);
    if (filters.date_to) params.append("date_to", filters.date_to);
    if (filters.category !== "all") params.append("category", filters.category);
    if (filters.department !== "all") params.append("department", filters.department);
    if (filters.payment_method !== "all") params.append("payment_method", filters.payment_method);
    if (filters.search) params.append("search", filters.search);
    try {
      const res = await api.get(`/expenses/export/${type}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `expenses_export.${type === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const pieData = useMemo(() => dashboard?.category_breakdown?.map((c) => ({ name: c.category, value: c.total })) || [], [dashboard]);
  const trendData = useMemo(() => dashboard?.monthly_trend?.map((t) => ({ month: t.month.slice(2), total: t.total })) || [], [dashboard]);

  return (
    <DashboardLayout title="Expense Tracker">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="expense-dashboard-cards">
        <SummaryCard icon={CalendarDays} label="This Month" value={currency(dashboard?.monthly_total)} testId="card-monthly-total" />
        <SummaryCard icon={Wallet} label="This Year" value={currency(dashboard?.yearly_total)} testId="card-yearly-total" />
        <SummaryCard icon={LayoutGrid} label="Top Category" value={dashboard?.category_breakdown?.[0]?.category || "—"} sub={dashboard?.category_breakdown?.[0] ? currency(dashboard.category_breakdown[0].total) : ""} testId="card-top-category" />
        <SummaryCard icon={Receipt} label="Total Records" value={dashboard?.total_count ?? 0} testId="card-total-count" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="expense-pie-chart">
          <p className="text-sm font-semibold text-[#1C1C1E] mb-3">Category-wise Spending (This Year)</p>
          {pieData.length === 0 ? (
            <p className="text-sm text-[#4B5563] text-center py-16">No data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => currency(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="expense-trend-chart">
          <p className="text-sm font-semibold text-[#1C1C1E] mb-3">Monthly Trend (Last 12 Months)</p>
          {trendData.length === 0 ? (
            <p className="text-sm text-[#4B5563] text-center py-16">No data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#4B5563" }} />
                <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} />
                <Tooltip formatter={(v) => currency(v)} />
                <Line type="monotone" dataKey="total" stroke="#00823B" strokeWidth={2.5} dot={{ fill: "#76C121", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#4B5563]" />
          <Input data-testid="expense-search-input" placeholder="Search title, vendor, reference..." value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="pl-8 w-56 h-9" />
        </div>
        <Input data-testid="expense-date-from-filter" type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="w-36 h-9" />
        <Input data-testid="expense-date-to-filter" type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="w-36 h-9" />
        <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v }))}>
          <SelectTrigger data-testid="expense-category-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{options.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.department} onValueChange={(v) => setFilters((f) => ({ ...f, department: v }))}>
          <SelectTrigger data-testid="expense-department-filter" className="w-36 h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Departments</SelectItem>{options.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.payment_method} onValueChange={(v) => setFilters((f) => ({ ...f, payment_method: v }))}>
          <SelectTrigger data-testid="expense-payment-filter" className="w-36 h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Methods</SelectItem>{options.payment_methods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex-1" />
        <Button size="sm" variant="outline" data-testid="expense-export-excel-button" onClick={() => exportFile("excel")}><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
        <Button size="sm" variant="outline" data-testid="expense-export-pdf-button" onClick={() => exportFile("pdf")}><FileText className="h-3.5 w-3.5 mr-1" /> PDF</Button>
        {isAdmin && (
          <Button size="sm" data-testid="add-expense-button" onClick={() => { setEditingExpense(null); setFormOpen(true); }} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Expense
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto" data-testid="expenses-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-4 py-3 font-medium whitespace-nowrap">Date</th>
              <th className="px-4 py-3 font-medium">Title</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Method</th>
              <th className="px-4 py-3 font-medium">Vendor</th>
              <th className="px-4 py-3 font-medium">Last Updated</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#4B5563]">No expenses match the current filters.</td></tr>}
            {expenses.map((e) => (
              <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">{e.expense_date}</td>
                <td className="px-4 py-3 font-medium text-[#1C1C1E]">{e.title}</td>
                <td className="px-4 py-3"><span className="text-xs font-medium text-[#00823B] bg-[#76C121]/10 px-2 py-0.5 rounded-md">{e.category}</span></td>
                <td className="px-4 py-3 text-[#4B5563]">{e.department}</td>
                <td className="px-4 py-3 font-medium text-[#1C1C1E]">{currency(e.amount)}</td>
                <td className="px-4 py-3 text-[#4B5563]">{e.payment_method}</td>
                <td className="px-4 py-3 text-[#4B5563]">{e.vendor || "—"}</td>
                <td className="px-4 py-3 text-[#4B5563] text-xs">{e.updated_by ? `${e.updated_by} · ${new Date(e.updated_at).toLocaleDateString()}` : `${e.created_by} (created)`}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {e.attachment_path && (
                      <>
                        <a href={fileUrl(e.attachment_path)} target="_blank" rel="noreferrer" data-testid={`expense-attachment-view-${e.id}`} title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                        <a href={downloadUrl(e.attachment_path)} data-testid={`expense-attachment-${e.id}`} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                        {isAdmin && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`expense-attachment-delete-${e.id}`} className="text-red-600" title="Remove attachment"><Trash2 className="h-4 w-4" /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove this attachment?</AlertDialogTitle>
                                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteAttachment(e.id)} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </>
                    )}
                    {isAdmin && (
                      <>
                        <button data-testid={`edit-expense-${e.id}`} onClick={() => { setEditingExpense(e); setFormOpen(true); }} className="text-[#4B5563] hover:text-[#1C1C1E]"><Pencil className="h-4 w-4" /></button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`delete-expense-${e.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{e.title}"?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteExpense(e.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                    <ReconciliationNoteButton entityType="expense" entityId={e.id} label={e.title} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ExpenseFormDialog open={formOpen} onOpenChange={setFormOpen} options={options} expense={editingExpense} onSaved={refreshAll} />
    </DashboardLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-start justify-between">
      <div>
        <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-1">{label}</p>
        <p className="text-xl font-bold text-[#1C1C1E]">{value}</p>
        {sub && <p className="text-xs text-[#00823B] mt-0.5">{sub}</p>}
      </div>
      <div className="h-9 w-9 rounded-md bg-[#76C121]/10 flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5 text-[#00823B]" />
      </div>
    </div>
  );
}
